/**
 * Day and week boundaries (docs/game-design.md §2.7). Weeks start Monday 00:00 UTC. Days follow
 * the player's time zone (their country's for a registered player, G3; the device's for a guest).
 * League seasons (§7.3) are 15 calendar days from LEAGUES.firstSeasonStart in that time zone.
 */
import { LEAGUES } from '../config/leagues';

const DAY_MS = 86_400_000;

function isoDate(utcMs: number): string {
  const d = new Date(utcMs);
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${m}-${day}`;
}

function parseIsoDate(key: string): number {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Start of the week (Monday 00:00 UTC) containing `nowMs`. */
export function weekStartUtc(nowMs: number): number {
  const d = new Date(nowMs);
  const sinceMonday = (d.getUTCDay() + 6) % 7;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - sinceMonday * DAY_MS;
}

export function weekKey(nowMs: number): string {
  return isoDate(weekStartUtc(nowMs));
}

export function previousWeekKey(key: string): string {
  return isoDate(parseIsoDate(key) - 7 * DAY_MS);
}

/** Next weekly reset (Monday 00:00 UTC) after `nowMs`. */
export function nextWeekResetUtc(nowMs: number): number {
  return weekStartUtc(nowMs) + 7 * DAY_MS;
}

const dayFormats = new Map<string, Intl.DateTimeFormat>();

/** Calendar day "YYYY-MM-DD" in a time zone (falls back to UTC for unknown zones). */
export function dayKey(nowMs: number, timeZone: string): string {
  let fmt = dayFormats.get(timeZone);
  if (!fmt) {
    try {
      fmt = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    dayFormats.set(timeZone, fmt);
  }
  return fmt.format(nowMs);
}

export function previousDayKey(key: string): string {
  return isoDate(parseIsoDate(key) - DAY_MS);
}

/** Days since 1970-01-01 of a "YYYY-MM-DD" date. */
export function dayIndex(date: string): number {
  return Math.round(parseIsoDate(date) / DAY_MS);
}

/** The "YYYY-MM-DD" date `index` days after 1970-01-01. */
export function dateOfIndex(index: number): string {
  return isoDate(index * DAY_MS);
}

export interface LeagueDay {
  /** 1, 2, …; 0 before the first season. */
  season: number;
  /** 1–15; 0 before the first season. */
  day: number;
}

/** The league season and day of a calendar date (docs/game-design.md §7.3). */
export function leagueDayOf(date: string): LeagueDay {
  const offset = dayIndex(date) - dayIndex(LEAGUES.firstSeasonStart);
  if (offset < 0) return { season: 0, day: 0 };
  return { season: Math.floor(offset / LEAGUES.seasonDays) + 1, day: (offset % LEAGUES.seasonDays) + 1 };
}

/** The calendar date of a season's day. */
export function dateOfLeagueDay(season: number, day: number): string {
  return dateOfIndex(dayIndex(LEAGUES.firstSeasonStart) + (season - 1) * LEAGUES.seasonDays + day - 1);
}

/** The league season at `nowMs` in a time zone (the free skill reset is once per season, §5.3). */
export function seasonKey(nowMs: number, timeZone = 'UTC'): number {
  return leagueDayOf(dayKey(nowMs, timeZone)).season;
}
